import { z } from "zod";
import { operatorContextSchema } from "@proof/authorization";
const hash = z.string().regex(/^[a-f0-9]{64}$/),
  label = z.string().min(1).max(200),
  text = (maximum: number) =>
    z
      .string()
      .max(maximum)
      .refine(
        (value) =>
          !/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(value),
      );
export const handoffInputSchema = z
  .object({
    jobId: label,
    idempotencyKey: z
      .string()
      .min(16)
      .max(128)
      .regex(/^[a-zA-Z0-9_-]+$/),
    versionId: label.optional(),
    name: text(100).trim().min(1),
    approval: z
      .object({
        accepted: z.literal(true),
        initials: text(16).trim().min(1),
        comments: text(4000).default(""),
      })
      .strict(),
  })
  .strict();
export type HandoffInput = z.infer<typeof handoffInputSchema>;
export const handoffRecordSchema = z
  .object({
    version: z.literal(1),
    id: z.string().regex(/^handoff_[a-f0-9]{64}$/),
    tenantId: label,
    ownerSubjectId: label,
    operatorContextId: label.optional(),
    idempotencyKey: handoffInputSchema.shape.idempotencyKey,
    requestHash: hash,
    name: handoffInputSchema.shape.name,
    createdAt: z.string().datetime(),
    approval: handoffInputSchema.shape.approval,
    source: z
      .object({
        jobId: label,
        renderKey: hash,
        projectId: label,
        projectName: text(100),
        versionId: label.optional(),
        partId: label.optional(),
        renderedAt: z.string().datetime(),
        artifactSha256: hash,
        artifactBytes: z.number().int().positive().max(268435456),
        artifactFilename: text(200),
        colorSpace: z.enum(["rgb", "cmyk"]),
        printProfile: z
          .object({
            name: text(120),
            sha256: hash,
            bytes: z.number().int().positive().max(4194304),
          })
          .optional(),
      })
      .strict(),
    package: z
      .object({
        key: hash,
        sha256: hash,
        bytes: z.number().int().positive().max(268435456),
      })
      .strict(),
    identityMode: z.literal("portable"),
    pressReady: z.literal(false),
  })
  .strict();
export type HandoffRecord = z.infer<typeof handoffRecordSchema>;
export type HandoffSummary = Omit<
  HandoffRecord,
  "idempotencyKey" | "requestHash" | "approval"
> & { approval: { accepted: true; initials: string } };

const deliveryReference = text(200).trim().min(1);
export const handoffDeliveryInputSchema = z
  .object({ targetId: deliveryReference })
  .strict();
export const handoffDeliveryReceiptSchema = z
  .object({
    version: z.literal(1),
    deliveryId: z.string().regex(/^delivery_[a-f0-9]{64}$/),
    requestHash: hash,
    tenantId: label,
    subjectId: label,
    operatorContextId: label.optional(),
    handoffId: handoffRecordSchema.shape.id,
    packageSha256: hash,
    targetId: deliveryReference,
    orderId: deliveryReference,
    lineId: deliveryReference,
    committedAt: z.string().datetime(),
  })
  .strict();
export const handoffDeliveryResultSchema = z
  .object({
    receipt: handoffDeliveryReceiptSchema,
    replayed: z.boolean(),
  })
  .strict();
export const handoffDeliveryStatusSchema = z.discriminatedUnion("available", [
  z.object({ available: z.literal(false) }).strict(),
  z
    .object({
      available: z.literal(true),
      id: z
        .string()
        .min(1)
        .max(200)
        .regex(/^[a-zA-Z0-9][a-zA-Z0-9_.:-]*$/),
      label: text(100).trim().min(1),
    })
    .strict(),
]);
export type HandoffDeliveryInput = z.infer<typeof handoffDeliveryInputSchema>;
export type HandoffDeliveryReceipt = z.infer<
  typeof handoffDeliveryReceiptSchema
>;
export type HandoffDeliveryResult = z.infer<typeof handoffDeliveryResultSchema>;
export type HandoffDeliveryStatus = z.infer<typeof handoffDeliveryStatusSchema>;
export const handoffDeliveryIntentSchema = handoffDeliveryReceiptSchema
  .omit({
    orderId: true,
    lineId: true,
    committedAt: true,
  })
  .extend({
    packageBytes: handoffRecordSchema.shape.package.shape.bytes,
    projectId: label,
    versionId: label.optional(),
    operatorContext: operatorContextSchema.optional(),
  })
  .strict()
  .refine((value) =>
    !value.operatorContext
      ? !value.operatorContextId
      : value.operatorContextId === value.operatorContext.id &&
        value.tenantId === value.operatorContext.tenantId &&
        value.subjectId === value.operatorContext.effectiveSubjectId,
  );
export type HandoffDeliveryIntent = z.infer<typeof handoffDeliveryIntentSchema>;
